import { DownArrow, Menu, MenuButton, MenuContainer, MenuItem, TextContainer, UpArrow } from './Selector.styles';
import { useCallback, useEffect, useState } from 'preact/hooks';

import { h } from 'preact';
import { useKeyPress } from '../../hooks';

interface Props<T> {
  onChange: (selected: T) => void;
  items: SelectItem<T>[];
  ariaLabel?: string;
  defaultItem?: SelectItem<T>;
  id?: string;
  isExpandedOnInit?: boolean;
  maxHeight?: number;
  testIdContainer?: string;
  testIdMenu?: string;
  testIdMenuButton?: string;
}

type SelectItem<T> = {
  displayName: string;
  value: T;
};

function Selector<T>({
  onChange,
  items,
  ariaLabel,
  defaultItem,
  id,
  isExpandedOnInit,
  maxHeight,
  testIdContainer,
  testIdMenu,
  testIdMenuButton,
}: Props<T>): h.JSX.Element {
  const [dropdownExpanded, setDropdownExpanded] = useState(isExpandedOnInit);
  const [selectedItem, setSelected] = useState(defaultItem || items[0]);
  const isEscapePressed = useKeyPress({ targetKey: 'Escape' });

  const clickListener = useCallback(
    (event: MouseEvent) => {
      // eslint-disable-next-line
      const clickPath = ((event as any).path as EventTarget[]) || (event.composedPath && event.composedPath());

      if (clickPath) {
        // eslint-disable-next-line
        const isPartOfLanguageSelector = clickPath.some((elem) => (elem as any).id === id);

        if (!isPartOfLanguageSelector) {
          setDropdownExpanded(false);
        }
      }
    },
    [id],
  );

  useEffect(() => {
    if (isEscapePressed && dropdownExpanded) {
      setDropdownExpanded(!dropdownExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEscapePressed]);

  useEffect(() => {
    document.addEventListener('click', clickListener);

    return (): void => {
      document.removeEventListener('click', clickListener);
    };
  }, [clickListener]);

  useEffect(() => {
    setSelected(defaultItem || items[0]);
  }, [defaultItem, items]);

  const onClickHandler = useCallback(
    (selectItem: SelectItem<T>): void => {
      setSelected(selectItem);
      onChange(selectItem.value);
    },
    [onChange],
  );

  return (
    <MenuContainer data-testid={testIdContainer} id={id}>
      <MenuButton
        data-testid={testIdMenuButton}
        onClick={(): void => {
          setDropdownExpanded(!dropdownExpanded);
        }}
      >
        <TextContainer>{selectedItem.displayName}</TextContainer>
        {dropdownExpanded ? <UpArrow /> : <DownArrow />}
      </MenuButton>
      {dropdownExpanded && (
        <Menu aria-label={ariaLabel} data-testid={testIdMenu} maxHeight={maxHeight}>
          {items.map((item) => {
            return (
              <MenuItem
                data-testid={`uc-menu-item-${item.displayName}`}
                onClick={(): void => {
                  setDropdownExpanded(!dropdownExpanded);
                  onClickHandler(item);
                }}
              >
                {item.displayName}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </MenuContainer>
  );
}

Selector.defaultProps = {
  ariaLabel: null,
  defaultItem: null,
  id: '',
  isExpandedOnInit: false,
  maxHeight: 100,
  testIdContainer: '',
  testIdMenu: '',
  testIdMenuButton: '',
};
export default Selector;
